import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { formatMoney, formatPlacedDate, formatPlacedTime } from '../lib/format';
import { addonLineTotal, orderDisplayNumber, humanOrderStatus } from '../lib/orders';
import { pickErrorMessage } from '../lib/apiHelpers';
import {
  humanPaymentStatus,
  pickInvoiceFromPayment,
  pickLinkedOrder,
  pickMerchantOrderId,
  pickPaymentAmount,
  pickPaymentAppUserId,
  pickPaymentCurrency,
  pickPaymentCustomerEmail,
  pickPaymentCustomerName,
  pickPaymentCustomerPhone,
  pickPaymentDeliveryAddress,
  pickPaymentDeliveryPreference,
  pickPaymentDraft,
  pickPaymentGateway,
  pickPaymentLineItems,
  pickPaymentSettledAt,
  pickPaymentStatusHistory,
  pickLineItemOptions,
} from '../lib/payments';
import { PageHeader, Card, CardBody, ErrorBanner, BackLink } from '../components/PageChrome';
import { IconClock, IconMapPin, IconPhone, IconUser } from '../components/Icons';

function paymentStatusBadge(status) {
  const s = String(status || '').toUpperCase();
  const map = { SUCCESS: 'success', FAILED: 'danger' };
  const c = map[s] || 'secondary';
  return <span className={`badge text-bg-${c}`}>{humanPaymentStatus(s)}</span>;
}

function lineItemQty(li) {
  const q = li?.quantity ?? 1;
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function lineItemUnitPrice(li) {
  const u = li?.menuItemOriginalPrice ?? li?.unitPrice ?? li?.price;
  if (u == null) return null;
  const n = Number(u);
  return Number.isFinite(n) ? n : null;
}

function lineItemTotal(li) {
  const t = li?.lineTotal;
  if (t != null) {
    const n = Number(t);
    if (Number.isFinite(n)) return n;
  }
  const u = lineItemUnitPrice(li);
  if (u == null) return null;
  return u * lineItemQty(li);
}

export default function PaymentDetail() {
  const { paymentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await adminApi.getPayment(paymentId);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  const draft = useMemo(() => pickPaymentDraft(data || {}), [data]);
  const lineItems = useMemo(() => pickPaymentLineItems(data || {}), [data]);
  const address = useMemo(() => pickPaymentDeliveryAddress(data || {}), [data]);
  const deliveryPref = useMemo(() => pickPaymentDeliveryPreference(data || {}), [data]);
  const linkedOrder = useMemo(() => pickLinkedOrder(data || {}), [data]);
  const invoice = useMemo(() => pickInvoiceFromPayment(data || {}), [data]);
  const statusHistory = useMemo(() => pickPaymentStatusHistory(data || {}), [data]);
  const merchantRef = useMemo(() => pickMerchantOrderId(data || {}), [data]);
  const settledAt = useMemo(() => pickPaymentSettledAt(data || {}), [data]);
  const amount = useMemo(() => pickPaymentAmount(data || {}), [data]);
  const currency = useMemo(() => pickPaymentCurrency(data || {}), [data]);
  const gateway = useMemo(() => pickPaymentGateway(data || {}), [data]);
  const customerName = useMemo(() => pickPaymentCustomerName(data || {}), [data]);
  const customerEmail = useMemo(() => pickPaymentCustomerEmail(data || {}), [data]);
  const customerPhone = useMemo(() => pickPaymentCustomerPhone(data || {}), [data]);
  const appUserId = useMemo(() => pickPaymentAppUserId(data || {}), [data]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (!data && !error) {
    return (
      <>
        <BackLink to="/payments">Payments</BackLink>
        <p className="text-secondary">Payment not found.</p>
      </>
    );
  }

  const isSuccess = String(data?.status || '').toUpperCase() === 'SUCCESS';
  const orderNo = linkedOrder ? orderDisplayNumber(linkedOrder) : '';
  const orderId = linkedOrder?.orderId ?? data?.orderId ?? '';

  return (
    <>
      <BackLink to="/payments">Payments</BackLink>
      <PageHeader
        title={`Payment ${merchantRef || paymentId}`}
        subtitle={
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            {paymentStatusBadge(data?.status)}
            {gateway ? <span className="badge rounded-pill text-bg-light border">{gateway}</span> : null}
            {settledAt ? (
              <span className="text-secondary small d-inline-block">
                <span className="d-block text-dark">{formatPlacedDate(settledAt)}</span>
                <time className="d-block order-placed-time" dateTime={settledAt}>
                  {formatPlacedTime(settledAt)}
                </time>
              </span>
            ) : null}
          </span>
        }
      />
      <ErrorBanner message={error} />

      <div className="row g-3 g-lg-4">
        <div className="col-lg-7 col-xl-8">
          <Card className="mb-3 overflow-hidden border-0 shadow-sm">
            <CardBody className="p-0">
              <div className="order-detail-items-head px-3 px-md-4 py-3">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-0">Checkout items</h2>
                <p className="small text-secondary mb-0 mt-1">What the customer attempted to pay for</p>
              </div>
              {lineItems.length === 0 ? (
                <div className="p-4 text-secondary small border-top">No line items on this payment.</div>
              ) : (
                <div className="admin-table-shell order-detail-items-shell mb-0">
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0 admin-data-table order-detail-items-table">
                      <thead>
                        <tr>
                          <th scope="col">Item</th>
                          <th scope="col" className="text-end">
                            Qty
                          </th>
                          <th scope="col" className="text-end">
                            Each
                          </th>
                          <th scope="col" className="text-end">
                            Line
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li, idx) => {
                          const rowKey = li.menuItemId ?? idx;
                          const pq = lineItemQty(li);
                          const options = pickLineItemOptions(li);
                          return (
                            <React.Fragment key={rowKey}>
                              <tr>
                                <td>
                                  <div className="fw-semibold text-dark">{li.name ?? 'Item'}</div>
                                  {li.notes ? <div className="small text-secondary mt-1">{li.notes}</div> : null}
                                </td>
                                <td className="text-end tabular-nums text-secondary">{pq}</td>
                                <td className="text-end tabular-nums text-secondary">{formatMoney(lineItemUnitPrice(li))}</td>
                                <td className="text-end tabular-nums fw-semibold text-dark">{formatMoney(lineItemTotal(li))}</td>
                              </tr>
                              {options.map((opt, oi) => (
                                <tr key={`${rowKey}-opt-${oi}`} className="order-line-addon-row">
                                  <td className="ps-md-4">
                                    <span className="order-line-addon-prefix text-secondary" aria-hidden>
                                      +
                                    </span>
                                    <span className="small text-secondary">{opt.name}</span>
                                  </td>
                                  <td className="text-end small text-secondary">—</td>
                                  <td className="text-end tabular-nums small text-secondary">
                                    {opt.price != null ? formatMoney(opt.price) : '—'}
                                  </td>
                                  <td className="text-end tabular-nums small text-secondary">{formatMoney(addonLineTotal(opt, pq))}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {statusHistory.length > 0 ? (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Payment timeline</h2>
                <ul className="list-unstyled mb-0 small">
                  {statusHistory.map((entry, i) => (
                    <li key={i} className="d-flex justify-content-between gap-3 py-2 border-bottom">
                      <span className="fw-medium">{humanPaymentStatus(entry.status)}</span>
                      <span className="text-secondary text-nowrap">
                        {entry.at ? formatPlacedDate(entry.at) : '—'}
                        {entry.at ? ` · ${formatPlacedTime(entry.at)}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}
        </div>

        <div className="col-lg-5 col-xl-4">
          <Card className="mb-3 order-detail-eta shadow-sm">
            <CardBody className="d-flex gap-3 align-items-center p-3 p-md-4">
              <div className="order-detail-eta__icon" aria-hidden>
                <IconClock size={20} strokeWidth={2} />
              </div>
              <div>
                <div className="small text-secondary text-uppercase fw-semibold mb-1">Amount charged</div>
                <div className="h5 mb-0 text-dark fw-bold">
                  {formatMoney(amount)}
                  {currency && currency !== 'INR' ? (
                    <span className="small text-secondary fw-normal ms-1">{currency}</span>
                  ) : null}
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody className="p-3 p-md-4">
              <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Payment details</h2>
              <dl className="row small mb-0">
                <dt className="col-5 text-secondary">Payment ID</dt>
                <dd className="col-7 text-end text-break font-monospace">{data?.paymentId ?? '—'}</dd>
                <dt className="col-5 text-secondary">Checkout ref</dt>
                <dd className="col-7 text-end text-break">{merchantRef || '—'}</dd>
                <dt className="col-5 text-secondary">Gateway</dt>
                <dd className="col-7 text-end">{gateway || '—'}</dd>
                {data?.gatewayOrderId ? (
                  <>
                    <dt className="col-5 text-secondary">PG order</dt>
                    <dd className="col-7 text-end text-break font-monospace small">{data.gatewayOrderId}</dd>
                  </>
                ) : null}
                {data?.gatewayPaymentId ? (
                  <>
                    <dt className="col-5 text-secondary">PG payment</dt>
                    <dd className="col-7 text-end text-break font-monospace small">{data.gatewayPaymentId}</dd>
                  </>
                ) : null}
                {data?.gatewayTransactionId ? (
                  <>
                    <dt className="col-5 text-secondary">Transaction</dt>
                    <dd className="col-7 text-end text-break font-monospace small">{data.gatewayTransactionId}</dd>
                  </>
                ) : null}
                {!isSuccess && data?.failureReason ? (
                  <>
                    <dt className="col-5 text-secondary">Failure</dt>
                    <dd className="col-7 text-end text-danger text-break">{data.failureReason}</dd>
                  </>
                ) : null}
                {!isSuccess && data?.failureCode ? (
                  <>
                    <dt className="col-5 text-secondary">Code</dt>
                    <dd className="col-7 text-end font-monospace small">{data.failureCode}</dd>
                  </>
                ) : null}
              </dl>
            </CardBody>
          </Card>

          <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Invoice</h2>
                <dl className="row small mb-0">
                  <dt className="col-5 text-secondary">Invoice no.</dt>
                  <dd className="col-7 text-end">{invoice.invoiceNumber || '—'}</dd>
                  <dt className="col-5 text-secondary">Generated</dt>
                  <dd className="col-7 text-end">
                    {invoice.invoiceGeneratedAt ? (
                      <>
                        <span className="d-block">{formatPlacedDate(invoice.invoiceGeneratedAt)}</span>
                        <time className="d-block order-placed-time" dateTime={String(invoice.invoiceGeneratedAt)}>
                          {formatPlacedTime(invoice.invoiceGeneratedAt)}
                        </time>
                      </>
                    ) : (
                      '—'
                    )}
                  </dd>
                  <dt className="col-5 text-secondary">PDF</dt>
                  <dd className="col-7 text-end">
                    {invoice.invoiceUrl ? (
                      <a
                        href={invoice.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        Download
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </dl>
              </CardBody>
          </Card>

          {linkedOrder || orderId ? (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Linked order</h2>
                {orderNo ? <div className="fw-semibold text-dark mb-1">{orderNo}</div> : null}
                {linkedOrder?.status ? (
                  <div className="small text-secondary mb-2">Status: {humanOrderStatus(linkedOrder.status)}</div>
                ) : null}
                {orderId ? (
                  <Link to={`/orders/${orderId}`} className="btn btn-sm btn-outline-primary">
                    View order
                  </Link>
                ) : (
                  <p className="small text-secondary mb-0">No order was created for this payment.</p>
                )}
              </CardBody>
            </Card>
          ) : null}

          {draft ? (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Checkout totals</h2>
                <dl className="row small mb-0">
                  <dt className="col-6 text-secondary">Subtotal</dt>
                  <dd className="col-6 text-end tabular-nums">{formatMoney(draft.subtotal)}</dd>
                  <dt className="col-6 text-secondary">Delivery</dt>
                  <dd className="col-6 text-end tabular-nums">{formatMoney(draft.deliveryFee)}</dd>
                  <dt className="col-6 text-secondary">Service fee</dt>
                  <dd className="col-6 text-end tabular-nums">{formatMoney(draft.serviceFee)}</dd>
                  <dt className="col-6 text-secondary">Tip</dt>
                  <dd className="col-6 text-end tabular-nums">{formatMoney(draft.tipAmount)}</dd>
                  <dt className="col-6 text-secondary">Discount</dt>
                  <dd className="col-6 text-end tabular-nums text-success">
                    {draft.discountAmount ? `−${formatMoney(draft.discountAmount)}` : '—'}
                  </dd>
                  {draft.promoCode ? (
                    <>
                      <dt className="col-6 text-secondary">Promo</dt>
                      <dd className="col-6 text-end">{draft.promoCode}</dd>
                    </>
                  ) : null}
                  <dt className="col-6 fw-semibold text-dark pt-2">Total</dt>
                  <dd className="col-6 text-end tabular-nums fw-bold text-dark pt-2">{formatMoney(draft.total ?? amount)}</dd>
                </dl>
              </CardBody>
            </Card>
          ) : null}

          <Card className="mb-3">
            <CardBody className="p-3 p-md-4">
              <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Customer</h2>
              <div className="fw-semibold text-dark">{customerName || '—'}</div>
              {customerEmail ? (
                <div className="small text-break mt-1">
                  <a href={`mailto:${customerEmail}`}>{customerEmail}</a>
                </div>
              ) : null}
              {customerPhone ? (
                <div className="small mt-1">
                  <a href={`tel:${String(customerPhone).replace(/\s/g, '')}`}>{customerPhone}</a>
                </div>
              ) : null}
              {appUserId ? (
                <div className="mt-3">
                  <Link to={`/app-users/${appUserId}`} className="btn btn-sm btn-outline-primary">
                    App profile
                  </Link>
                </div>
              ) : null}
            </CardBody>
          </Card>

          {deliveryPref?.estimatedTimeRange ? (
            <Card className="mb-3 order-detail-eta shadow-sm">
              <CardBody className="d-flex gap-3 align-items-center p-3 p-md-4">
                <div className="order-detail-eta__icon" aria-hidden>
                  <IconClock size={20} strokeWidth={2} />
                </div>
                <div>
                  <div className="small text-secondary text-uppercase fw-semibold mb-1">Delivery window</div>
                  <div className="h6 mb-0 text-dark fw-bold">{deliveryPref.estimatedTimeRange}</div>
                </div>
              </CardBody>
            </Card>
          ) : null}

          {address ? (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex gap-3">
                  <div className="order-detail-eta__icon" style={{ width: '2.5rem', height: '2.5rem' }} aria-hidden>
                    <IconMapPin size={18} />
                  </div>
                  <div className="min-w-0 flex-grow-1">
                    <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-2">Delivery address</h2>
                    {address.name || address.contactNumber ? (
                      <div className="border-bottom pb-2 mb-2">
                        {address.name ? (
                          <div className="d-flex align-items-center gap-2 fw-semibold text-dark">
                            <IconUser size={15} className="text-secondary flex-shrink-0" />
                            <span className="text-break">{address.name}</span>
                          </div>
                        ) : null}
                        {address.contactNumber ? (
                          <div className="d-flex align-items-center gap-2 small text-secondary mt-1">
                            <IconPhone size={14} className="text-secondary flex-shrink-0" />
                            <a
                              href={`tel:${String(address.contactNumber).replace(/\s/g, '')}`}
                              className="text-reset text-decoration-none"
                            >
                              {address.contactNumber}
                            </a>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {address.label ? <div className="fw-semibold text-dark mb-1">{address.label}</div> : null}
                    {address.lines.map((ln, i) => (
                      <div key={i} className="small text-secondary text-break">
                        {ln}
                      </div>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          ) : null}

        </div>
      </div>
    </>
  );
}
